import * as React from 'react';
import { styled } from '../../theme';
import { ButtonType } from '../buttons/ButtonWidget';
import { useSystem } from '../../hooks/useSystem';
import { HelperTextWidget } from '../forms/HelperTextWidget';
import { ENV } from '../../env';
import { CenterFormOrientation, CenterFormWidget } from '../layout/CenterFormWidget';
import { FormLayoutWidget } from '../layout/FormLayoutWidget';
import { GroupedButtons } from '../buttons/GroupedButtons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { copyTextToClipboard } from '@journeyapps-platform/ide-lib-reactor-utils';
import { saveFile } from '../../utils/file';

export type BackupCodesDisplayWidgetProps = {
  codes: string[];
  onClose: () => void;
};

namespace S {
  export const Container = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
  `;

  export const CodesList = styled.ul`
    margin-top: 30px;
    margin-bottom: 30px;
    color: ${(p) => p.theme.text.primary};
  `;

  export const BackupCodeItem = styled.li`
    user-select: text;
  `;

  export const Text = styled(HelperTextWidget)`
    max-width: 350px;
  `;
}

export function formatBackupCodes(codes: string[]) {
  return `
BACKUP VERIFICATION CODES
${ENV.USER_EMAIL}

${codes.map((code, index) => `${index + 1}. ${code}`).join('\n')}

Date: ${new Date()}

Running out of backup codes? Generate new ones:
https://accounts.journeyapps.com/portal/profile/security/configure-mfa
Only the latest set of backup codes will work. 
  `;
}

export const BackupCodesDisplayWidget: React.FC<BackupCodesDisplayWidgetProps> = (props) => {
  const { codes } = props;

  const system = useSystem();

  return (
    <CenterFormWidget noLogo={true} primaryLabel={'Backup Codes'} orientation={CenterFormOrientation.ROW}>
      <FormLayoutWidget
        sections={[
          {
            key: 'content',
            elements: [
              <S.Container key={'content'}>
                <S.Text>
                  Backup codes can be used to authenticate in the event you cannot receive multi-factor authentication
                  codes.
                </S.Text>
                <S.CodesList>
                  {codes.map((code) => (
                    <S.BackupCodeItem key={code}>{code}</S.BackupCodeItem>
                  ))}
                </S.CodesList>
                <GroupedButtons
                  btns={[
                    {
                      label: 'Copy to clipboard',
                      icon: <FontAwesomeIcon icon={'clipboard'} />,
                      type: ButtonType.SECONDARY,
                      action: () => {
                        if (copyTextToClipboard(codes.join('\n'))) {
                          return system.dialogStore.infoDialog({
                            message: 'Backup codes have been copied to clipboard.'
                          });
                        }
                        system.dialogStore.errorDialog({
                          errorMessage: 'Backup codes could not be copied to clipboard.'
                        });
                      }
                    },
                    {
                      label: 'Download',
                      icon: <FontAwesomeIcon icon={'download'} />,
                      type: ButtonType.PRIMARY,
                      action: () => {
                        saveFile(new TextEncoder().encode(formatBackupCodes(codes)), 'backup-codes.txt');
                      }
                    }
                  ]}
                />
              </S.Container>
            ]
          },
          {
            key: 'buttons',
            elements: [
              <GroupedButtons
                key="buttons"
                btns={[
                  {
                    label: 'Close',
                    type: ButtonType.TERTIARY,
                    action: props.onClose
                  }
                ]}
              />
            ]
          }
        ]}
      />
    </CenterFormWidget>
  );
};
